import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { DAYS_OF_WEEK, LayoutGroup, LayoutGroupRequest } from '../models/layout.model';
import { Modal } from '../../../shared/components/modal/modal';
import { ModalFooterDirective } from '../../../shared/components/modal/modal-footer.directive';
import { FormField } from '../../../shared/components/forms/form-field';
import { TextInput } from '../../../shared/components/forms/text-input';
import { NumberInput } from '../../../shared/components/forms/number-input';
import { Toggle } from '../../../shared/components/forms/toggle';

export type DialogMode = 'add' | 'edit';

@Component({
  selector: 'app-layout-group-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, Modal, ModalFooterDirective, FormField, TextInput, NumberInput, Toggle],
  templateUrl: './layout-group-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LayoutGroupDialogComponent implements OnChanges {
  private readonly fb = inject(FormBuilder);

  @Input() mode: DialogMode = 'add';
  @Input() group: LayoutGroup | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<LayoutGroupRequest>();

  readonly daysOfWeek = DAYS_OF_WEEK;
  readonly open = signal(true);

  form = this.fb.group({
    groupId: ['', Validators.required],
    startDate: [''],
    endDate: [''],
    startTime: [''],
    endTime: [''],
    days: [[] as string[]],
    isTemplateSlideshow: [false],
    templateSlideshowChangePeriod: [null as number | null],
  });

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['group']) {
      if (this.group) {
        this.form.patchValue({
          groupId: this.group.groupId,
          startDate: this.group.startDate ?? '',
          endDate: this.group.endDate ?? '',
          startTime: this.group.startTime ?? '',
          endTime: this.group.endTime ?? '',
          days: this.group.days ? this.group.days.split(',') : [],
          isTemplateSlideshow: this.group.isStateSlideshow ?? false,
          templateSlideshowChangePeriod: this.group.stateSlideshowChangePeriod ?? null,
        });
      } else {
        this.form.reset({ isTemplateSlideshow: false, days: [] });
      }
    }
  }

  isDaySelected(day: string): boolean {
    return ((this.form.value.days as string[]) ?? []).includes(day);
  }

  toggleDay(day: string): void {
    const current = (this.form.value.days as string[]) ?? [];
    const updated = current.includes(day)
      ? current.filter(d => d !== day)
      : [...current, day];
    this.form.patchValue({ days: updated });
  }

  onClose(): void {
    this.close.emit();
  }

  onSubmit(): void {
    if (this.form.invalid) return;
    const v = this.form.value;
    this.save.emit({
      groupId: v.groupId!,
      startDate: v.startDate || null,
      endDate: v.endDate || null,
      startTime: v.startTime || null,
      endTime: v.endTime || null,
      days: (v.days as string[]).length > 0 ? (v.days as string[]) : null,
      isTemplateSlideshow: v.isTemplateSlideshow ?? null,
      templateSlideshowChangePeriod: v.templateSlideshowChangePeriod ?? null,
    });
  }
}
